// src/components/programs/SportsProgram.jsx
import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import SportsImage from "../../assets/Programs/Pillar1.png"; // Replace with actual sports image
import { Link } from "react-router-dom";

export default function SportsProgram() {
  const [showSelection, setShowSelection] = useState(false);
  const [level, setLevel] = useState("");
  const [weeks, setWeeks] = useState("");
  const panelRef = useRef(null);

  const bulletPoints = [
    "Enhance sport-specific skills with targeted drills.",
    "Improve agility, speed, and coordination safely.",
    "Increase endurance and functional strength for performance.",
    "Follow structured cycles to optimize progression.",
    "Use recovery protocols to prevent injuries.",
    "Track performance metrics for measurable improvement."
  ];

  useEffect(() => {
    if (showSelection && panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect();
      const panelTop = rect.top + window.scrollY;
      const panelBottom = rect.bottom + window.scrollY;
      const viewportHeight = window.innerHeight;

      // Only scroll if the panel is not fully visible
      if (rect.bottom > viewportHeight) {
        const scrollY = panelBottom - viewportHeight + 20; // 20px padding
        window.scrollTo({ top: scrollY, behavior: "smooth" });
      } else if (rect.top < 0) {
        const scrollY = panelTop - 20; // scroll up slightly if top is off-screen
        window.scrollTo({ top: scrollY, behavior: "smooth" });
      }
    }
  }, [showSelection]);

  return (
    <section id= "sports_program" className="w-full bg-black/40 py-24 flex flex-col items-center relative">

      {/* Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[55%] h-[40%] bg-neonTurquoise/10 blur-[120px] rounded-full pointer-events-none"></div>

      {/* CONTENT */}
      <div className="w-full max-w-[80%] mx-auto flex flex-col md:flex-row gap-16 items-start relative z-20">
        
        {/* IMAGE */}
        <motion.div
          className="md:w-1/2 flex justify-center"
          whileHover={{ scale: 1.03 }}
        >
          <img
            src={SportsImage}
            alt="Sports Program"
            className="rounded-2xl shadow-[0_0_30px_rgba(0,255,255,0.25)] w-full object-cover"
          />
        </motion.div>

        {/* TEXT */}
        <motion.div
          className="md:w-1/2 flex flex-col gap-6"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-white text-5xl font-bold tracking-wide drop-shadow-xl">
            Sport-Specific Programs
          </h2>

          <p className="text-gray-300 text-lg leading-relaxed">
            These programs use structured stages to enhance sport-specific performance, improve coordination, and strengthen functional abilities safely through progressive cycles.
          </p>

          <p className="text-gray-300 text-lg leading-relaxed">
            Athletes learn to monitor performance, manage recovery, and follow targeted plans. Suitable for beginners and advanced athletes aiming for measurable performance gains and injury prevention.
          </p>

          <h3 className="text-neonTurquoise text-2xl font-semibold">
            What you will build in this plan:
          </h3>

          <ul className="flex flex-col gap-3 text-gray-300 text-lg ml-4">
            {bulletPoints.map((item, i) => (
              <li key={i} className="leading-relaxed flex">
                <span className="text-neonTurquoise mr-2">•</span>
                {item}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>

      {/* SELECT BUTTON */}
      <motion.button
        onClick={() => setShowSelection(!showSelection)}
        whileHover={{ scale: 1.05 }}
        className="mt-16 px-10 py-4 bg-neonTurquoise text-black font-bold rounded-full text-lg shadow-lg hover:bg-neonTurquoise/80 transition tracking-wide"
      >
        Select Your Sports Plan
      </motion.button>

      {/* PANEL */}
      {showSelection && (
        <motion.div
          ref={panelRef}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="
            mt-10
            backdrop-blur-xl
            bg-white/10
            border border-neonTurquoise/40
            rounded-2xl
            px-10 py-8
            shadow-[0_0_35px_rgba(0,255,255,0.25)]
            flex flex-col md:flex-row
            gap-10 items-center
            w-full max-w-[45%] mx-auto
          "
        >
          {/* LEVEL SELECT */}
          <div className="flex flex-col items-start gap-2 w-full max-w-xs">
            <label className="text-white font-semibold text-lg">Level</label>
            <div className="relative w-full">
              <select
                className="
                  bg-gray-900 text-white w-full px-4 py-3 rounded-xl
                  border border-gray-700 shadow
                  focus:ring-2 focus:ring-neonTurquoise
                  outline-none appearance-none
                "
                value={level}
                onChange={(e) => setLevel(e.target.value)}
              >
                <option value="">Select Level</option>
                <option value="beginner">Beginner</option>
                <option value="advanced">Advanced</option>
              </select>
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none">
                ▼
              </span>
            </div>
          </div>

          {/* WEEKS SELECT */}
          <div className="flex flex-col items-start gap-2 w-full max-w-xs">
            <label className="text-white font-semibold text-lg">Duration</label>
            <div className="relative w-full">
              <select
                className="
                  bg-gray-900 text-white w-full px-4 py-3 rounded-xl
                  border border-gray-700 shadow
                  focus:ring-2 focus:ring-neonTurquoise
                  outline-none appearance-none
                "
                value={weeks}
                onChange={(e) => setWeeks(e.target.value)}
              >
                <option value="">Select Weeks</option>
                <option value="6">6 Weeks</option>
                <option value="8">8 Weeks</option>
                <option value="12">12 Weeks</option>
              </select>
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none">
                ▼
              </span>
            </div>
          </div>

          {/* VIEW PLAN */}
          {level && weeks && (
            <Link
              to={`/sports/${level}?weeks=${weeks}`} // points to beginner or advanced sports plan pages
              className="px-8 py-3 bg-neonTurquoise text-black font-bold rounded-full shadow-lg hover:bg-neonTurquoise/80 transition whitespace-nowrap"
            >
              View Plan
            </Link>
          )}
        </motion.div>
      )}
    </section>
  );
}
